<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-purple-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-purple-600 tracking-widest">Administración</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Catálogo de Servicios Públicos
          <span class="px-2 sm:px-3 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 text-xs sm:text-sm font-black rounded-lg border border-purple-200 dark:border-purple-800">
            {{ servicios.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <button @click="cargarServicios" :disabled="loading" class="px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-sm flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" class="text-base" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
        <button @click="openCreateModal" class="px-3 sm:px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nuevo Servicio</span>
          <span class="sm:hidden">Nuevo</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-6">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Buscar</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-purple-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Nombre, tipo..." class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base" />
          </div>
        </div>
        <div class="sm:col-span-4">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Tipo</label>
          <select v-model="filterTipo" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option v-for="t in tiposDisponibles" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>
        <div class="sm:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Estado</label>
          <select v-model="filterEstado" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option value="activo">ACTIVOS</option>
            <option value="inactivo">INACTIVOS</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Grid de tarjetas -->
    <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
      <div v-for="i in 8" :key="'sk' + i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
        <div class="h-12 bg-gray-100 dark:bg-gray-700 rounded-xl mb-3"></div>
        <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    </div>

    <div v-else-if="filteredServicios.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-16 text-center">
      <div class="flex flex-col items-center opacity-30 select-none">
        <Icon icon="mdi:shape-outline" class="text-7xl mb-3" />
        <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay servicios</h4>
        <p class="text-sm text-gray-300 mt-1">Registra tu primer servicio público</p>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
      <div v-for="servicio in paginatedServicios" :key="servicio.idServicioPublico"
           class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all group">
        <div class="p-5">
          <div class="flex items-start justify-between mb-4">
            <div class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm"
                 :style="{ backgroundColor: getColorServicio(servicio) + '20' }">
              <Icon :icon="getIconoServicio(servicio)"
                    class="text-2xl"
                    :style="{ color: getColorServicio(servicio) }" />
            </div>
            <span :class="[
              'px-2.5 py-1 text-[9px] font-black uppercase tracking-widest rounded-lg border',
              servicio.estado === 'activo'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
            ]">
              {{ servicio.estado }}
            </span>
          </div>
          <h3 class="text-base font-black text-gray-900 dark:text-white mb-1 truncate">{{ servicio.nombre }}</h3>
          <p class="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">{{ servicio.tipo_servicio }}</p>
          <p v-if="servicio.descripcion" class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">{{ servicio.descripcion }}</p>
          <div class="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
            <Icon icon="mdi:file-document-multiple" class="text-indigo-500 text-sm" />
            <span class="text-[11px] font-black text-gray-600 dark:text-gray-400">{{ servicio.totalContratos || 0 }} contratos</span>
          </div>
        </div>
        <div class="px-3 pb-3 flex items-center gap-1.5">
          <button @click="openEditModal(servicio)" class="flex-1 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-lg font-black text-[10px] tracking-widest hover:bg-amber-500 hover:text-white transition-all flex items-center justify-center gap-1">
            <Icon icon="mdi:pencil" class="text-sm" /> Editar
          </button>
          <button
            @click="abrirModalToggleStatus(servicio)"
            :class="[
              'flex-1 py-2 rounded-lg font-black text-[10px] tracking-widest transition-all flex items-center justify-center gap-1',
              servicio.estado === 'activo'
                ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-500 hover:text-white'
                : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500 hover:text-white'
            ]">
            <Icon :icon="servicio.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-sm" />
            {{ servicio.estado === 'activo' ? 'Desactivar' : 'Activar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- PAGINACIÓN -->
    <!-- ============================================ -->
    <div v-if="filteredServicios.length > 0 && !loading"
         class="mt-4 sm:mt-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 px-3 sm:px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">

      <!-- Info + selector de items por página -->
      <div class="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          Mostrando <span class="text-purple-600">{{ startItem }}</span> - <span class="text-purple-600">{{ endItem }}</span>
          de <span class="text-purple-600">{{ filteredServicios.length }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:inline">Mostrar:</span>
          <select v-model.number="itemsPerPage" @change="currentPage = 1"
                  class="px-2 py-1 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 rounded-lg text-[11px] font-black text-gray-700 dark:text-white outline-none cursor-pointer">
            <option :value="8">8</option>
            <option :value="12">12</option>
            <option :value="24">24</option>
            <option :value="48">48</option>
            <option :value="96">96</option>
          </select>
        </div>
      </div>

      <!-- Controles de navegación -->
      <div class="flex items-center gap-1 flex-wrap justify-center">
        <button @click="currentPage = 1" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Primera página">
          <Icon icon="mdi:page-first" class="text-base sm:text-lg" />
        </button>

        <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Anterior">
          <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
        </button>

        <div class="hidden sm:flex items-center gap-0.5 mx-1">
          <button v-for="page in visiblePages" :key="page"
                  @click="currentPage = page"
                  :class="['min-w-[34px] h-9 rounded-lg text-[11px] font-black transition-all flex items-center justify-center',
                    currentPage === page
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-500/30'
                      : 'text-gray-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 dark:hover:border-purple-800']">
            {{ page }}
          </button>
        </div>

        <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2 whitespace-nowrap">
          Pág. {{ currentPage }} / {{ totalPages }}
        </span>

        <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Siguiente">
          <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
        </button>

        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Última página">
          <Icon icon="mdi:page-last" class="text-base sm:text-lg" />
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORM -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-purple-600 to-pink-600 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editing ? 'mdi:pencil-box' : 'mdi:plus-box'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ editing ? 'Editar Servicio' : 'Nuevo Servicio Público' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ editing ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="saveServicio" class="space-y-5">
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre *</label>
                <input v-model="form.nombre" type="text" required maxlength="100" class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" placeholder="Ej: Electricidad Local 1, Agua Casa..." />
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tipo de Servicio *</label>
                <select v-model="form.tipo_servicio" required class="w-full px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer">
                  <option v-for="t in TIPOS_DISPONIBLES" :key="t.value" :value="t.value">{{ t.label }}</option>
                </select>
              </div>

              <!-- Preview + Selector de icono + Color -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Icono</label>
                  <div class="flex items-center gap-3">
                    <div class="w-14 h-14 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm border-2 border-gray-200 dark:border-gray-600"
                         :style="{ backgroundColor: (form.color || '#6366f1') + '20' }">
                      <Icon :icon="form.icono || 'mdi:shape'"
                            class="text-3xl"
                            :style="{ color: form.color || '#6366f1' }" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <button type="button" @click="abrirSelectorIconos"
                              class="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 hover:border-purple-500 rounded-xl text-xs font-black text-gray-700 dark:text-white transition-all flex items-center justify-center gap-2">
                        <Icon icon="mdi:emoticon-outline" class="text-base" />
                        Elegir Icono
                      </button>
                      <p class="text-[9px] text-gray-400 mt-1 text-center truncate">{{ form.icono || 'Sin seleccionar' }}</p>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Color</label>
                  <div class="flex items-center gap-3">
                    <input v-model="form.color" type="color"
                           class="w-14 h-14 rounded-xl border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 cursor-pointer bg-transparent" />
                    <div class="flex-1">
                      <input v-model="form.color" type="text" maxlength="7"
                             class="w-full px-3 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-lg text-xs font-bold text-gray-800 dark:text-white outline-none uppercase" />
                      <div class="flex gap-1 mt-2 flex-wrap">
                        <button v-for="c in coloresRapidos" :key="c" type="button"
                                @click="form.color = c"
                                class="w-6 h-6 rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:scale-110 transition-transform"
                                :style="{ backgroundColor: c }"
                                :title="c"></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Descripción</label>
                <textarea v-model="form.descripcion" rows="2" maxlength="200" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none" placeholder="Descripción opcional"></textarea>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Estado</label>
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" v-model="form.estado" value="activo" class="w-4 h-4 text-purple-600" /> Activo
                  </label>
                  <label class="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" v-model="form.estado" value="inactivo" class="w-4 h-4 text-purple-600" /> Inactivo
                  </label>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="closeModal" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
                <button type="submit" :disabled="saving" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-purple-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{ 'animate-spin': saving }" />
                  {{ saving ? 'Guardando...' : (editing ? 'Actualizar Servicio' : 'Crear Servicio') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL SELECTOR DE ICONOS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="mostrarSelectorIconos"
           class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10003]"
           @click.self="cerrarSelectorIconos">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden animate-zoomIn flex flex-col">

          <!-- Header -->
          <div class="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex items-center gap-3 flex-shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:emoticon-happy-outline" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-black text-white">Selector de Iconos</h3>
              <p class="text-[10px] text-white/70 font-bold">Elige el icono que mejor represente tu servicio</p>
            </div>
            <button @click="cerrarSelectorIconos" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>

          <!-- Buscador -->
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex-shrink-0">
            <div class="relative group">
              <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-purple-500 transition-colors" />
              <input v-model="iconoSearch"
                     type="text"
                     placeholder="Buscar icono... (ej: agua, luz, wifi, router)"
                     class="w-full pl-12 pr-10 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl outline-none dark:text-white font-bold text-sm" />
              <button v-if="iconoSearch" type="button" @click="iconoSearch = ''"
                      class="absolute right-3 top-1/2 -translate-y-1/2 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                <Icon icon="mdi:close" class="text-gray-500 text-sm" />
              </button>
            </div>
            <!-- Filtros por categoría -->
            <div class="flex gap-2 mt-3 flex-wrap">
              <button type="button" @click="filtroCategoriaIcono = 'all'"
                      :class="[
                        'px-3 py-1.5 rounded-lg text-[10px] font-black tracking-widest transition-all',
                        filtroCategoriaIcono === 'all'
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-700'
                      ]">
                TODOS
              </button>
              <button v-for="cat in categoriasIconos" :key="cat.value"
                      type="button"
                      @click="filtroCategoriaIcono = cat.value"
                      :class="[
                        'px-3 py-1.5 rounded-lg text-[10px] font-black tracking-widest transition-all uppercase',
                        filtroCategoriaIcono === cat.value
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-700'
                      ]">
                {{ cat.label }}
              </button>
            </div>
          </div>

          <!-- Grid de iconos -->
          <div class="p-4 overflow-y-auto flex-1">
            <div v-if="iconosFiltrados.length === 0" class="text-center py-10">
              <Icon icon="mdi:emoticon-sad-outline" class="text-5xl text-gray-300 mx-auto mb-3" />
              <p class="text-sm font-black text-gray-400">No se encontraron iconos</p>
              <p class="text-xs text-gray-400 mt-1">Intenta con otra palabra</p>
            </div>

            <div v-else class="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              <button v-for="ic in iconosFiltrados"
                      :key="ic.value"
                      type="button"
                      @click="seleccionarIcono(ic.value)"
                      :class="[
                        'aspect-square rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-1 p-1 group',
                        form.icono === ic.value
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-md scale-105'
                          : 'border-gray-200 dark:border-gray-700 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/20 hover:scale-105'
                      ]"
                      :title="ic.label">
                <Icon :icon="ic.value"
                      :class="[
                        'text-2xl transition-all',
                        form.icono === ic.value
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-gray-600 dark:text-gray-300 group-hover:text-purple-500'
                      ]" />
                <span class="text-[8px] font-black text-gray-400 truncate w-full text-center px-0.5">{{ ic.label }}</span>
              </button>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col sm:flex-row gap-3 items-center flex-shrink-0">
            <div class="flex items-center gap-3 flex-1">
              <div class="w-12 h-12 rounded-xl flex items-center justify-center border-2 border-gray-200 dark:border-gray-700 flex-shrink-0"
                   :style="{ backgroundColor: (form.color || '#6366f1') + '20' }">
                <Icon :icon="form.icono || 'mdi:shape'" class="text-2xl"
                      :style="{ color: form.color || '#6366f1' }" />
              </div>
              <div class="min-w-0">
                <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Seleccionado</p>
                <p class="text-xs font-bold text-gray-700 dark:text-white truncate">{{ form.icono || 'Sin seleccionar' }}</p>
              </div>
            </div>
            <button @click="cerrarSelectorIconos"
                    class="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-md flex items-center justify-center gap-2">
              <Icon icon="mdi:check" class="text-base" />
              Listo
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIRMAR TOGGLE STATUS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10002]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div :class="[
            'px-6 py-4',
            confirmTarget?.estado === 'activo'
              ? 'bg-gradient-to-r from-red-600 to-orange-600'
              : 'bg-gradient-to-r from-emerald-600 to-teal-600'
          ]">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmTarget?.estado === 'activo' ? 'mdi:close-circle' : 'mdi:check-circle'" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-sm font-black text-white">
                  {{ confirmTarget?.estado === 'activo' ? 'Desactivar Servicio' : 'Activar Servicio' }}
                </h3>
                <p class="text-[10px] text-white/80">Cambiar estado del servicio</p>
              </div>
            </div>
          </div>

          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">
              ¿{{ confirmTarget?.estado === 'activo' ? 'Desactivar' : 'Activar' }} el servicio
              <strong>"{{ confirmTarget?.nombre }}"</strong>?
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Pasará de <strong>"{{ confirmTarget?.estado }}"</strong> a
              <strong>"{{ confirmTarget?.estado === 'activo' ? 'inactivo' : 'activo' }}"</strong>.
            </p>
          </div>

          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest">Cancelar</button>
            <button @click="ejecutarToggleStatus" :disabled="confirmLoading"
                    :class="[
                      'flex-[2] py-3 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 transition-all',
                      confirmTarget?.estado === 'activo'
                        ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                    ]">
              <Icon :icon="confirmLoading ? 'mdi:loading' : (confirmTarget?.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye')" :class="{ 'animate-spin': confirmLoading }" />
              {{ confirmLoading ? 'Procesando...' : (confirmTarget?.estado === 'activo' ? 'Desactivar' : 'Activar') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';

import {
  obtenerServiciosPublicos,
  crearServicioPublico,
  actualizarServicioPublico,
  TIPOS_DISPONIBLES,
  COLORES_FALLBACK,
  ICONOS_FALLBACK,
  getColorServicio,
  getIconoServicio
} from '@/api/servicioPublico.js';

export default {
  name: 'ServicioPublico',
  components: { Icon },
  data() {
    return {
      servicios: [],
      loading: false,
      saving: false,
      showModal: false,
      editing: null,
      searchQuery: '',
      filterTipo: 'all',
      filterEstado: 'all',
      TIPOS_DISPONIBLES,
      form: {
        nombre: '',
        tipo_servicio: 'agua',
        descripcion: '',
        icono: 'mdi:water',
        color: '#00bcd4',
        estado: 'activo'
      },

      // Paginación
      currentPage: 1,
      itemsPerPage: 12,

      // Selector de iconos
      mostrarSelectorIconos: false,
      iconoSearch: '',
      filtroCategoriaIcono: 'all',

      // Colores rápidos
      coloresRapidos: [
        '#00bcd4', '#2196f3', '#3f51b5', '#9c27b0',
        '#e91e63', '#f44336', '#ff5722', '#ff9800',
        '#ffc107', '#4caf50', '#009688', '#607d8b'
      ],

      // Catálogo de iconos disponibles
      iconosDisponibles: [
        // Agua
        { value: 'mdi:water', label: 'Agua', categoria: 'agua' },
        { value: 'mdi:water-pump', label: 'Bomba agua', categoria: 'agua' },
        { value: 'mdi:water-well', label: 'Pozo', categoria: 'agua' },
        { value: 'mdi:water-percent', label: 'Humedad', categoria: 'agua' },
        { value: 'mdi:pipe', label: 'Tubería', categoria: 'agua' },
        // Electricidad
        { value: 'mdi:lightning-bolt', label: 'Rayo', categoria: 'luz' },
        { value: 'mdi:flash', label: 'Flash', categoria: 'luz' },
        { value: 'mdi:power-plug', label: 'Enchufe', categoria: 'luz' },
        { value: 'mdi:transmission-tower', label: 'Torre', categoria: 'luz' },
        { value: 'mdi:lightbulb', label: 'Bombillo', categoria: 'luz' },
        { value: 'mdi:led-strip-variant', label: 'LED', categoria: 'luz' },
        // Internet
        { value: 'mdi:wifi', label: 'Wi-Fi', categoria: 'internet' },
        { value: 'mdi:router-wireless', label: 'Router', categoria: 'internet' },
        { value: 'mdi:access-point-network', label: 'Access Point', categoria: 'internet' },
        { value: 'mdi:ethernet', label: 'Ethernet', categoria: 'internet' },
        { value: 'mdi:cloud', label: 'Nube', categoria: 'internet' },
        { value: 'mdi:server-network', label: 'Servidor', categoria: 'internet' },
        { value: 'mdi:signal', label: 'Señal', categoria: 'internet' },
        // Teléfono
        { value: 'mdi:phone', label: 'Teléfono', categoria: 'telefono' },
        { value: 'mdi:phone-classic', label: 'Tel. clásico', categoria: 'telefono' },
        { value: 'mdi:cellphone', label: 'Celular', categoria: 'telefono' },
        { value: 'mdi:phone-in-talk', label: 'En llamada', categoria: 'telefono' },
        // Gas
        { value: 'mdi:fire', label: 'Fuego', categoria: 'gas' },
        { value: 'mdi:gas-burner', label: 'Quemador', categoria: 'gas' },
        { value: 'mdi:propane-tank', label: 'Tanque gas', categoria: 'gas' },
        { value: 'mdi:fire-circle', label: 'Fuego círculo', categoria: 'gas' },
        // Cable / TV
        { value: 'mdi:television', label: 'TV', categoria: 'cable' },
        { value: 'mdi:satellite-variant', label: 'Satélite', categoria: 'cable' },
        { value: 'mdi:television-classic', label: 'TV clásica', categoria: 'cable' },
        { value: 'mdi:broadcast', label: 'Broadcast', categoria: 'cable' },
        // Aseo
        { value: 'mdi:trash-can', label: 'Basurero', categoria: 'aseo' },
        { value: 'mdi:broom', label: 'Escoba', categoria: 'aseo' },
        { value: 'mdi:delete-variant', label: 'Reciclaje', categoria: 'aseo' },
        // Otros
        { value: 'mdi:file-document', label: 'Documento', categoria: 'otro' },
        { value: 'mdi:home-city', label: 'Casa', categoria: 'otro' },
        { value: 'mdi:office-building', label: 'Edificio', categoria: 'otro' },
        { value: 'mdi:currency-usd', label: 'Dólar', categoria: 'otro' },
        { value: 'mdi:cart', label: 'Carrito', categoria: 'otro' },
        { value: 'mdi:tools', label: 'Herramientas', categoria: 'otro' },
        { value: 'mdi:shield-check', label: 'Seguridad', categoria: 'otro' },
        { value: 'mdi:alert-circle', label: 'Alerta', categoria: 'otro' },
        { value: 'mdi:information', label: 'Info', categoria: 'otro' },
        { value: 'mdi:star', label: 'Estrella', categoria: 'otro' },
        { value: 'mdi:store', label: 'Tienda', categoria: 'otro' },
        { value: 'mdi:factory', label: 'Fábrica', categoria: 'otro' },
        { value: 'mdi:warehouse', label: 'Depósito', categoria: 'otro' },
        { value: 'mdi:bank', label: 'Banco', categoria: 'otro' },
        { value: 'mdi:email', label: 'Email', categoria: 'otro' },
        { value: 'mdi:message', label: 'Mensaje', categoria: 'otro' },
        { value: 'mdi:bell', label: 'Campana', categoria: 'otro' }
      ],

      // Confirmar toggle
      showConfirmModal: false,
      confirmTarget: null,
      confirmLoading: false
    };
  },
  computed: {
    categoriasIconos() {
      const cats = new Set(this.iconosDisponibles.map(ic => ic.categoria).filter(Boolean));
      const labels = {
        agua: 'Agua', luz: 'Luz', internet: 'Internet', telefono: 'Teléfono',
        gas: 'Gas', cable: 'Cable/TV', aseo: 'Aseo', otro: 'Otros'
      };
      return Array.from(cats).map(c => ({ value: c, label: labels[c] || c }));
    },
    iconosFiltrados() {
      const q = this.iconoSearch.trim().toLowerCase();
      let list = [...this.iconosDisponibles];
      if (this.filtroCategoriaIcono !== 'all') {
        list = list.filter(ic => ic.categoria === this.filtroCategoriaIcono);
      }
      if (q) {
        list = list.filter(ic =>
          ic.label.toLowerCase().includes(q) ||
          ic.value.toLowerCase().includes(q) ||
          ic.categoria.toLowerCase().includes(q)
        );
      }
      return list;
    },
    tiposDisponibles() {
      const tipos = new Set(this.servicios.map(s => s.tipo_servicio).filter(Boolean));
      return Array.from(tipos).map(t => ({
        value: t,
        label: t.charAt(0).toUpperCase() + t.slice(1)
      }));
    },
    filteredServicios() {
      let list = [...this.servicios];
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(s =>
          (s.nombre || '').toLowerCase().includes(q) ||
          (s.tipo_servicio || '').toLowerCase().includes(q) ||
          (s.descripcion || '').toLowerCase().includes(q)
        );
      }
      if (this.filterTipo !== 'all') list = list.filter(s => s.tipo_servicio === this.filterTipo);
      if (this.filterEstado !== 'all') list = list.filter(s => s.estado === this.filterEstado);
      return list;
    },

    // ============================================
    // PAGINACIÓN
    // ============================================
    paginatedServicios() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredServicios.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredServicios.length / this.itemsPerPage) || 1;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    startItem() {
      if (this.filteredServicios.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredServicios.length);
    }
  },
  mounted() {
    this.cargarServicios();
  },
  watch: {
    'form.tipo_servicio'(newTipo, oldTipo) {
      const iconoAnteriorDefault = ICONOS_FALLBACK[oldTipo];
      if (!oldTipo || !this.form.icono || this.form.icono === iconoAnteriorDefault) {
        if (ICONOS_FALLBACK[newTipo]) this.form.icono = ICONOS_FALLBACK[newTipo];
      }
      if (COLORES_FALLBACK[newTipo]) this.form.color = COLORES_FALLBACK[newTipo];
    },

    // Resetear página al cambiar filtros
    searchQuery() {
      this.currentPage = 1;
    },
    filterTipo() {
      this.currentPage = 1;
    },
    filterEstado() {
      this.currentPage = 1;
    }
  },
  methods: {
    getColorServicio,
    getIconoServicio,

    showSuccess(mensaje, titulo = '¡Listo!') {
      useToast().success(`${titulo} — ${mensaje}`, { timeout: 3000, position: 'top-right' });
    },
    showError(mensaje, titulo = 'Error') {
      useToast().error(`${titulo} — ${mensaje}`, { timeout: 4000, position: 'top-right' });
    },
    showWarning(mensaje, titulo = 'Atención') {
      useToast().warning(`${titulo} — ${mensaje}`, { timeout: 3500, position: 'top-right' });
    },

    // Selector de iconos
    abrirSelectorIconos() {
      this.iconoSearch = '';
      this.filtroCategoriaIcono = 'all';
      this.mostrarSelectorIconos = true;
    },
    cerrarSelectorIconos() {
      this.mostrarSelectorIconos = false;
      this.iconoSearch = '';
      this.filtroCategoriaIcono = 'all';
    },
    seleccionarIcono(iconoValue) {
      this.form.icono = iconoValue;
    },

    // CRUD
    async cargarServicios() {
      this.loading = true;
      try {
        const res = await obtenerServiciosPublicos();
        if (res.success) this.servicios = res.data || [];
        else this.showError(res.error || 'No se pudieron cargar los servicios');
      } catch (e) {
        console.error(e);
        this.showError('No se pudieron cargar los servicios');
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editing = null;
      this.iconoSearch = '';
      this.filtroCategoriaIcono = 'all';
      this.form = {
        nombre: '',
        tipo_servicio: 'agua',
        descripcion: '',
        icono: ICONOS_FALLBACK.agua,
        color: COLORES_FALLBACK.agua,
        estado: 'activo'
      };
      this.showModal = true;
    },
    openEditModal(servicio) {
      this.editing = servicio;
      this.iconoSearch = '';
      this.filtroCategoriaIcono = 'all';
      this.form = {
        nombre: servicio.nombre,
        tipo_servicio: servicio.tipo_servicio,
        descripcion: servicio.descripcion || '',
        icono: servicio.icono || ICONOS_FALLBACK[servicio.tipo_servicio] || 'mdi:shape',
        color: servicio.color || COLORES_FALLBACK[servicio.tipo_servicio] || '#6366f1',
        estado: servicio.estado || 'activo'
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editing = null;
      this.iconoSearch = '';
      this.filtroCategoriaIcono = 'all';
    },
    async saveServicio() {
      if (!this.form.nombre || !this.form.nombre.trim()) {
        this.showWarning('El nombre es obligatorio');
        return;
      }
      this.saving = true;
      try {
        let res;
        if (this.editing) {
          res = await actualizarServicioPublico(this.editing.idServicioPublico, this.form);
        } else {
          res = await crearServicioPublico(this.form);
        }
        if (res.success) {
          await this.cargarServicios();
          this.closeModal();
          this.showSuccess(this.editing ? 'Servicio actualizado correctamente' : 'Servicio creado correctamente');
        } else {
          this.showError(res.error || 'No se pudo guardar');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.saving = false;
      }
    },

    // ============================================
    // TOGGLE STATUS (Activar/Desactivar)
    // ============================================
    abrirModalToggleStatus(servicio) {
      this.confirmTarget = servicio;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmTarget = null;
      this.confirmLoading = false;
    },
    async ejecutarToggleStatus() {
      if (!this.confirmTarget) return;
      this.confirmLoading = true;
      const esActivo = this.confirmTarget.estado === 'activo';
      try {
        const payload = {
          nombre: this.confirmTarget.nombre,
          tipo_servicio: this.confirmTarget.tipo_servicio,
          descripcion: this.confirmTarget.descripcion,
          icono: this.confirmTarget.icono,
          color: this.confirmTarget.color,
          estado: esActivo ? 'inactivo' : 'activo'
        };
        const res = await actualizarServicioPublico(this.confirmTarget.idServicioPublico, payload);
        if (res.success) {
          await this.cargarServicios();
          this.showSuccess(`Servicio ${esActivo ? 'desactivado' : 'activado'} correctamente`);
        } else {
          this.showError(res.error || 'No se pudo cambiar el estado');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.confirmLoading = false;
        this.cancelarConfirmacion();
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
</style>